import { Tag, TagType } from '../types/tag';

// ============================================================================
// NEW PHASE 1: Layered Index System
// ============================================================================
// Phase 1 replaces the old 4-dim flat tags with:
//   1. Hierarchical Category Tree (primary + secondary)
//   2. Style Facets (limited, curated set)
//   3. Tool Soft Tags (user-creatable with suggestions)
// ============================================================================

// --- Category Tree (Hierarchical, result-oriented) ---
// Design: 7 first-level categories, each with a small curated set of leaves.
// Total: ~40 leaf nodes. MECE principle.

export interface CategoryNode {
    id: string;
    name: string;
    slug: string;
    children?: CategoryNode[];
}

export const CATEGORY_TREE: CategoryNode[] = [
    {
        id: 'cat_ai_video',
        name: 'AI 视频生成',
        slug: 'ai-video',
        children: [
            { id: 'cat_ad_tvc', name: '广告/TVC', slug: 'ad-tvc' },
            { id: 'cat_brand_film', name: '宣传片/品牌片', slug: 'brand-film' },
            { id: 'cat_ecom_video', name: '电商视频', slug: 'ecom-video' },
            { id: 'cat_short_film', name: '剧情短片/短剧', slug: 'short-film' },
            { id: 'cat_mv', name: '音乐视频 (MV)', slug: 'mv' },
            { id: 'cat_dynamic_logo', name: '动态 Logo/片头尾', slug: 'dynamic-logo' },
        ]
    },
    {
        id: 'cat_ai_image',
        name: 'AI 图像生成',
        slug: 'ai-image',
        children: [
            { id: 'cat_product_render', name: '产品白底图', slug: 'product-render' },
            { id: 'cat_product_lifestyle', name: '产品外景图', slug: 'product-lifestyle' },
            { id: 'cat_poster', name: '海报/运营图', slug: 'poster' },
            { id: 'cat_character_design', name: '角色原画/概念设计', slug: 'character-design' },
            { id: 'cat_scene_design', name: '场景/背景设计', slug: 'scene-design' },
            { id: 'cat_ui_asset', name: 'UI 素材/图标', slug: 'ui-asset' },
            { id: 'cat_illustration', name: '漫画/插画', slug: 'illustration' },
        ]
    },
    {
        id: 'cat_ai_audio',
        name: 'AI 音频',
        slug: 'ai-audio',
        children: [
            { id: 'cat_bgm', name: '背景音乐/配乐', slug: 'bgm' },
            { id: 'cat_sound_design', name: '音效设计', slug: 'sound-design' },
            { id: 'cat_tts', name: '语音合成/TTS', slug: 'tts' },
        ]
    },
    {
        id: 'cat_ai_text',
        name: 'AI 文本/内容',
        slug: 'ai-text',
        children: [
            { id: 'cat_copywriting', name: '广告文案', slug: 'copywriting' },
            { id: 'cat_script', name: '剧本/分镜脚本', slug: 'script' },
            { id: 'cat_prompt_eng', name: '提示词工程', slug: 'prompt-engineering' },
        ]
    },
    {
        id: 'cat_ai_training_services',
        name: 'AI 咨询/培训',
        slug: 'ai-training-services',
        children: [
            { id: 'cat_ai_training', name: 'AI 培训', slug: 'ai-training' },
        ]
    },
    {
        id: 'cat_integrated',
        name: '综合制作',
        slug: 'integrated',
        children: [
            { id: 'cat_brand_package', name: '品牌全案（视频+图+文案）', slug: 'brand-package' },
            { id: 'cat_game_art_kit', name: '游戏美术套件（角色+场景+UI）', slug: 'game-art-kit' },
            { id: 'cat_event_package', name: '节日/活动物料包', slug: 'event-package' },
        ]
    },
    {
        id: 'cat_tool_workflow',
        name: '工具/工作流',
        slug: 'tool-workflow',
        children: [
            { id: 'cat_comfy_workflow', name: '工作流搭建', slug: 'workflow-setup' },
            { id: 'cat_lora_training', name: 'LoRA/模型训练', slug: 'lora-training' },
            { id: 'cat_pipeline', name: '自动化生成 pipeline', slug: 'pipeline' },
            { id: 'cat_api_integration', name: 'API 集成/二次开发', slug: 'api-integration' },
        ]
    },
];

// Flatten leaf categories for convenience
export const ALL_LEAF_CATEGORIES: CategoryNode[] = CATEGORY_TREE.flatMap(cat =>
    cat.children ? cat.children.map(child => ({ ...child, parentName: cat.name })) : [{ ...cat }]
);

// --- Style Facets (Limited curated set, no custom creation) ---
export const STYLE_FACETS = [
    { id: 'sf_realistic', name: '写实', slug: 'realistic' },
    { id: 'sf_anime', name: '二次元', slug: 'anime' },
    { id: 'sf_chinese', name: '国风', slug: 'chinese' },
    { id: 'sf_scifi', name: '科幻', slug: 'scifi' },
    { id: 'sf_minimal', name: '极简', slug: 'minimal' },
    { id: 'sf_cinematic', name: '电影感', slug: 'cinematic' },
    { id: 'sf_brand', name: '品牌感', slug: 'brand' },
    { id: 'sf_watercolor', name: '水彩', slug: 'watercolor' },
    { id: 'sf_pixel', name: '像素风', slug: 'pixel' },
    { id: 'sf_cyberpunk', name: '赛博朋克', slug: 'cyberpunk' },
] as const;

// --- Tool Suggestions (Soft tags, user can create new ones) ---
export const TOOL_SUGGESTIONS = [
    'Midjourney',
    'Stable Diffusion',
    'Runway Gen-3',
    'Runway Gen-2',
    'Pika Labs',
    'ElevenLabs',
    'ChatGPT',
    'Claude',
    'ControlNet',
    'ComfyUI',
    'Adobe Firefly',
    'Sora',
    'Luma Dream Machine',
    '可灵 (Kling)',
    '即梦 (Jimeng)',
    '海螺 (Hailuo)',
    'FLUX',
    'DALL-E',
    'Udio',
    'Suno',
    'Hunyuan',
    'CogVideo',
    'Tripo',
    'Meshy',
] as const;

// --- Tool alias/normalization map for search ---
export const TOOL_ALIASES: Record<string, string[]> = {
    'Midjourney': ['MJ', 'Midjourney v6', 'Midjourney v5', 'MJ v6'],
    'Stable Diffusion': ['SD', 'SDXL', 'Stable Diffusion XL', 'SD 1.5'],
    'Runway Gen-3': ['Runway', 'RunwayML'],
    'ComfyUI': ['Comfy', 'Comfy 工作流'],
    '可灵 (Kling)': ['Kling', '可灵'],
    '即梦 (Jimeng)': ['Jimeng', '即梦'],
    '海螺 (Hailuo)': ['Hailuo', '海螺'],
};

// --- Synonym map for style search ---
export const STYLE_SYNONYMS: Record<string, string[]> = {
    '赛博朋克': ['Cyberpunk', '赛博', 'cyber'],
    '国风': ['水墨', '水墨画', '国画', '水墨风', '新中式', '国潮'],
    '二次元': ['日漫', '动漫', 'anime', 'Anime', 'manga'],
    '写实': ['Photorealistic', '超写实', '照片级', 'realistic'],
    '电影感': ['Cinematic', 'film look', '电影风格'],
    '极简': ['Minimalist', '冷淡风', '极简主义'],
    '像素风': ['Pixel Art', 'Pixel', '8-bit', '16-bit'],
    '水彩': ['Watercolor', '水彩画'],
};


// ============================================================================
// BACKWARD COMPATIBILITY: Old 4-dim flat tags (to be removed in Phase 2)
// ============================================================================
// These are kept for reference during migration. New code should use
// CATEGORY_TREE, STYLE_FACETS, and TOOL_SUGGESTIONS above.

export const CATEGORY_TAGS = [
    { id: 'cat_1', slug: 'ai-video', name: 'AI 视频生成' },
    { id: 'cat_2', name: 'AI 图像生成', slug: 'ai-image' },
    { id: 'cat_3', name: 'Prompt', slug: 'prompt' },
    { id: 'cat_4', name: 'LoRA', slug: 'lora' },
    { id: 'cat_5', name: 'AI 角色设计', slug: 'ai-character' },
    { id: 'cat_6', name: '语音合成 / TTS', slug: 'tts' },
    { id: 'cat_7', name: 'AI 音乐 / 音效', slug: 'ai-music' },
    { id: 'cat_8', name: 'AI 剧本创作', slug: 'ai-script' },
    { id: 'cat_9', name: '工作流自动化', slug: 'workflow' },
    { id: 'cat_10', name: 'AI 咨询服务', slug: 'ai-consulting' },
    { id: 'cat_11', name: '广告/TVC', slug: 'ad-tvc' },
    { id: 'cat_12', name: '剧情短片/短剧', slug: 'short-film' },
    { id: 'cat_13', name: '音乐视频 (MV)', slug: 'mv' },
    { id: 'cat_14', name: '宣传片/预告片', slug: 'trailer' },
    { id: 'cat_15', name: '动态Logo/片头', slug: 'dynamic-logo' },
    { id: 'cat_16', name: '电商视频', slug: 'e-commerce' },
    { id: 'cat_17', name: '概念设计/动态分镜', slug: 'concept-design' },
] as const;

export const TOOL_TAGS = [
    { id: 'tool_1', name: 'Midjourney', slug: 'midjourney' },
    { id: 'tool_2', name: 'Stable Diffusion', slug: 'stable-diffusion' },
    { id: 'tool_3', name: 'Runway Gen-2', slug: 'runway-gen2' },
    { id: 'tool_4', name: 'Runway Gen-3', slug: 'runway-gen3' },
    { id: 'tool_5', name: 'Pika Labs', slug: 'pika-labs' },
    { id: 'tool_6', name: 'ElevenLabs', slug: 'elevenlabs' },
    { id: 'tool_7', name: 'ChatGPT', slug: 'chatgpt' },
    { id: 'tool_8', name: 'Claude', slug: 'claude' },
    { id: 'tool_9', name: 'ControlNet', slug: 'controlnet' },
    { id: 'tool_10', name: 'ComfyUI', slug: 'comfyui' },
    { id: 'tool_11', name: 'Adobe Firefly', slug: 'adobe-firefly' },
    { id: 'tool_12', name: 'Sora', slug: 'sora' },
    { id: 'tool_13', name: 'Luma Dream Machine', slug: 'luma-dream-machine' },
    { id: 'tool_14', name: '可灵 (Kling)', slug: 'kling' },
    { id: 'tool_15', name: '即梦 (Jimeng)', slug: 'jimeng' },
    { id: 'tool_16', name: '海螺 (Hailuo)', slug: 'hailuo' },
] as const;

export const STYLE_TAGS = [
    { id: 'style_1', name: '赛博朋克', slug: 'cyberpunk' },
    { id: 'style_2', name: '日漫/二次元', slug: 'anime' },
    { id: 'style_3', name: '超写实/电影感', slug: 'photorealistic' },
    { id: 'style_4', name: '油画风格', slug: 'oil-painting' },
    { id: 'style_5', name: '3D渲染/C4D', slug: '3d-c4d' },
    { id: 'style_6', name: '极简/冷淡风', slug: 'minimalist' },
    { id: 'style_7', name: '像素风', slug: 'pixel-art' },
    { id: 'style_8', name: '水彩画', slug: 'watercolor' },
    { id: 'style_9', name: '复古/合成器波', slug: 'synthwave' },
    { id: 'style_12', name: '水墨/国画', slug: 'ink-painting' },
    { id: 'style_13', name: '黏土/定格动画', slug: 'claymation' },
    { id: 'style_14', name: '盲盒/Q版', slug: 'chibi' },
] as const;

export const TOPIC_TAGS = [
    { id: 'topic_1', name: '市场营销', slug: 'marketing' },
    { id: 'topic_2', name: '教育培训', slug: 'education' },
    { id: 'topic_3', name: '娱乐/影视', slug: 'entertainment' },
    { id: 'topic_4', name: '社交媒体', slug: 'social-media' },
    { id: 'topic_5', name: '电商/带货', slug: 'e-commerce-topic' },
    { id: 'topic_6', name: '游戏电竞', slug: 'gaming' },
    { id: 'topic_10', name: '科技/数码', slug: 'tech' },
    { id: 'topic_13', name: '科幻/未来', slug: 'sci-fi' },
    { id: 'topic_20', name: '国潮/新中式', slug: 'chinese-style' },
    { id: 'topic_21', name: 'Y2K/千禧风', slug: 'y2k' },
    { id: 'topic_22', name: '废土/末日', slug: 'wasteland' },
    { id: 'topic_23', name: '欧美复古', slug: 'western-retro' },
] as const;

// Helper to convert into Tag format for backward compatibility with Mock
const formatToTag = (arr: readonly any[], type: TagType): Tag[] =>
    arr.map(item => ({
        id: item.id,
        name: item.name,
        slug: item.slug,
        type: type,
        is_official: true,
        count: Math.floor(Math.random() * 500) // mock usage count
    }));

export const ALL_TAGS: Tag[] = [
    ...formatToTag(CATEGORY_TAGS, 'CATEGORY'),
    ...formatToTag(TOOL_TAGS, 'TOOL'),
    ...formatToTag(STYLE_TAGS, 'STYLE'),
    ...formatToTag(TOPIC_TAGS, 'TOPIC')
];

// For backward compatibility while refactoring mockTags usages
export const MOCK_TAGS: Tag[] = ALL_TAGS.map(t => ({
    ...t,
    type: (t.type === 'CATEGORY' ? 'Category' :
        t.type === 'TOOL' ? 'Tool' :
            t.type === 'STYLE' ? 'Style' : 'Topic') as TagType,
    usageCount: t.count
}));
